import type { ClassificationResult } from '@classification-testing/domain';
import { ClassificationType } from '@classification-testing/domain';
import { Badge, BadgeSize, BadgeTextSize } from '@soteria/design-system-ve';

import * as styles from './ClassificationResultDialogV2.css';

// Type for classification label (from ClassificationResult interface)
type ClassificationLabel = ClassificationResult['labels'][0];

// Helper to normalize content type for case-insensitive comparison
function normalizeContentType(contentType: string | undefined): string {
  return contentType?.toLowerCase() || 'unknown';
}

// Component for displaying triggered policies inline with a label
function TriggeredPoliciesInfo({ policies }: { policies: string[] }) {
  if (policies.length === 0) return null;

  return (
    <div className={styles.categoryMetric}>
      <span className={styles.categoryLabel}>Triggered:</span>
      <div className={styles.policyBadgeContainer}>
        {policies.map(policyName => (
          <Badge key={policyName} size={BadgeSize.XSmall} text={policyName} textSize={BadgeTextSize.Small} />
        ))}
      </div>
    </div>
  );
}

// Label Details component for V2
function LabelDetailsV2({ label, triggeredByPolicies }: { label: ClassificationLabel; triggeredByPolicies?: string[] }) {
  const normalizedType = normalizeContentType(label.content_type);
  const showSeverity = normalizedType === 'text' && label.severity !== undefined;
  const showScore = normalizedType === 'image' && label.score !== undefined;

  return (
    <div className={styles.categoryDetails}>
      {showSeverity && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Severity:</span>
          <span className={styles.severityValue}>{label.severity}</span>
        </div>
      )}
      {showScore && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Score:</span>
          <span className={styles.severityValue}>{label.score?.toFixed(2)}%</span>
        </div>
      )}
      {label.content_type && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Content Type:</span>
          <span className={styles.severityValue}>{label.content_type}</span>
        </div>
      )}
      {triggeredByPolicies && <TriggeredPoliciesInfo policies={triggeredByPolicies} />}
    </div>
  );
}

// Label Item component for V2 - colour-coded per label based on triggered policy status
function LabelItemV2({
  label,
  isTriggeredByPolicy,
  triggeredByPolicies,
}: {
  label: ClassificationLabel;
  isTriggeredByPolicy: boolean;
  triggeredByPolicies?: string[];
}) {
  const styleClass = isTriggeredByPolicy ? styles.categoryItemUnsafe : styles.categoryItemSafe;

  return (
    <div className={`${styles.categoryItem} ${styleClass}`}>
      <div className={styles.categoryInfo}>
        <span className={styles.categoryName}>{label.label_name}</span>
      </div>
      <LabelDetailsV2 label={label} triggeredByPolicies={triggeredByPolicies} />
    </div>
  );
}

// Build mapping from label IDs to the policy names that triggered them
export function buildPolicyMappings(policies: ClassificationResult['policies']) {
  const triggeredPolicyLabelIds = new Set<string>();
  const labelIdToPolicyNames = new Map<string, string[]>();

  if (policies && policies.length > 0) {
    policies.forEach(policy => {
      policy.labels.forEach(policyLabel => {
        triggeredPolicyLabelIds.add(policyLabel.label_id);
        const existingPolicies = labelIdToPolicyNames.get(policyLabel.label_id);

        if (existingPolicies) {
          labelIdToPolicyNames.set(policyLabel.label_id, [...existingPolicies, policy.name]);
        } else {
          labelIdToPolicyNames.set(policyLabel.label_id, [policy.name]);
        }
      });
    });
  }

  return { triggeredPolicyLabelIds, labelIdToPolicyNames };
}

// Group labels by content type
function groupLabelsByContentType(labels: ClassificationLabel[]) {
  const grouped = labels.reduce(
    (acc, label) => {
      const contentType = normalizeContentType(label.content_type);

      if (!acc[contentType]) {
        acc[contentType] = [];
      }

      acc[contentType].push(label);

      return acc;
    },
    {} as Record<string, ClassificationLabel[]>
  );

  const sortedTypes = Object.keys(grouped).sort((a, b) => {
    const order = { image: 0, text: 1, username: 2, audio: 3, mixed: 4 };

    return (order[a as keyof typeof order] ?? 99) - (order[b as keyof typeof order] ?? 99);
  });

  return { grouped, sortedTypes };
}

/**
 * Labels Section for V2 Wide Layout
 *
 * Matches V1 behaviour: each label is colour-coded based on whether it was triggered
 * by a policy (red border) or not (green border), and shows "Triggered:" with
 * policy badges inline per label.
 */
export function LabelsSectionV2({ result }: { result: ClassificationResult }) {
  if (!result.labels || result.labels.length === 0) return null;

  const { triggeredPolicyLabelIds, labelIdToPolicyNames } = buildPolicyMappings(result.policies);
  const hasPolicyMappings = triggeredPolicyLabelIds.size > 0;
  const isSafe = result.classificationType === ClassificationType.SAFE_CONTENT;
  const { grouped, sortedTypes } = groupLabelsByContentType(result.labels);

  return (
    <div className={`${styles.section} ${styles.labelsSection}`}>
      <h3 className={styles.sectionTitle}>Labels</h3>
      {sortedTypes.map(contentType => (
        <div key={contentType} className={styles.contentTypeGroup}>
          <div className={styles.contentTypeHeader}>Content Type: {contentType.toUpperCase()}</div>
          <div className={styles.categoriesList}>
            {grouped[contentType].map(label => {
              // Per-label colouring when policy-to-label mapping is available, otherwise fall back to overall isSafe
              const isTriggeredByPolicy = hasPolicyMappings && label.label_id ? triggeredPolicyLabelIds.has(label.label_id) : !isSafe;
              const triggeredByPolicies = label.label_id ? labelIdToPolicyNames.get(label.label_id) : undefined;

              return (
                <LabelItemV2
                  key={label.label_id || label.label_name}
                  isTriggeredByPolicy={isTriggeredByPolicy}
                  label={label}
                  triggeredByPolicies={triggeredByPolicies}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
